import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, Check, X, ArrowRight, Sparkle, Flame } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRO_FEATURES = [
  "Club-by-club averages dashboard",
  "Ad-free shareable round cards",
  "Premium tournament hosting (unlimited rounds)",
  "Priority WHS analytics with hole-by-hole tendencies",
  "Bula Pro badge on your profile + leaderboard",
];

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [mode, setMode] = useState(null);  // 'test' | 'live'
  const [founder, setFounder] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/pro/pricing").then((r) => {
      setPlans(r.data.plans);
      setMode(r.data.stripe_mode);
      setFounder(r.data.founder || null);
    }).catch(() => {});
  }, []);

  const checkout = async (planId) => {
    if (!user) { toast.error("Sign in first"); return; }
    setBusy(planId);
    try {
      const { data } = await api.post("/pro/checkout", {
        plan: planId, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-10">
      <div className="text-center max-w-2xl mx-auto rise">
        <span className="label-eyebrow inline-flex items-center gap-1.5">
          <Sparkle weight="fill" className="text-[#E05D3A]" /> Bula Pro
        </span>
        <h1 className="font-heading font-black text-5xl md:text-6xl tracking-tighter mt-2">
          Unlock your <span className="text-[#E05D3A]">edge</span>.
        </h1>
        <p className="text-[#0F2537]/70 mt-4 text-lg">
          Every Bula Pro user gets sharper stats, premium tournaments, and an ad-free shareable card. Cancel any time.
        </p>
        {user?.is_pro && (
          <div className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E5631] text-white text-sm font-bold" data-testid="already-pro-badge">
            <Crown weight="fill" /> You're Pro until {new Date(user.pro_expires_at).toLocaleDateString()}
          </div>
        )}
        {mode === "test" && (
          <div className="mt-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0F2537]/5 border border-dashed border-[#0F2537]/30 text-[#0F2537]/70 text-xs font-bold" data-testid="stripe-test-mode-badge">
            <Sparkle size={12} weight="fill" className="text-[#0F2537]/50" /> Stripe test mode — no real charges yet. Use card 4242 4242 4242 4242.
          </div>
        )}
      </div>

      {founder?.available && !user?.is_pro && (
        <FounderBanner founder={founder} />
      )}

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {plans.map((p) => {
          const yearly = p.id === "yearly";
          const founderActive = yearly && founder?.available && !user?.is_pro;
          const displayAmount = founderActive ? founder.founder_amount : p.amount;
          const tierLabel = founder?.tier_label || "Founder";
          const discountPct = founder?.discount_pct || 50;
          return (
            <div key={p.id} className={`relative card-default p-7 md:p-9 rise ${yearly ? "border-[#E05D3A]" : ""}`} data-testid={`plan-${p.id}`}>
              {yearly && !founderActive && (
                <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-[#E05D3A] text-white text-xs font-bold">
                  Save FJ${(19 * 12 - 169).toFixed(0)}/yr
                </div>
              )}
              {founderActive && (
                <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-[#E05D3A] text-white text-xs font-bold inline-flex items-center gap-1" data-testid="founder-tag">
                  <Flame weight="fill" size={12} /> {tierLabel} · {discountPct}% off Year 1
                </div>
              )}
              <div className="label-eyebrow">{p.label}</div>
              <div className="mt-3 font-heading font-black text-5xl tracking-tighter">
                {(p.currency || "fjd").toUpperCase() === "FJD" ? "FJ$" : "$"}{displayAmount}
                <span className="text-base font-bold text-[#0F2537]/60">/{yearly ? "year" : "month"}</span>
                {founderActive && (
                  <span className="ml-2 text-base font-bold text-[#0F2537]/40 line-through">FJ${p.amount}</span>
                )}
              </div>
              <div className="text-sm text-[#0F2537]/60 mt-1">
                {founderActive
                  ? `${p.days} days of Pro at the ${tierLabel.toLowerCase()} rate · renews at FJ$${p.amount}/yr · 7-day money-back guarantee`
                  : `${p.days} days of Pro · 7-day money-back guarantee`}
              </div>

              <ul className="mt-6 space-y-2">
                {PRO_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check weight="bold" className="text-[#1E5631] shrink-0 mt-0.5" size={16} /> {f}
                  </li>
                ))}
              </ul>

              <button onClick={() => checkout(p.id)} disabled={busy === p.id} data-testid={`checkout-${p.id}`}
                className={`mt-7 w-full py-3 rounded-xl font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60 ${
                  yearly ? "bg-[#E05D3A] text-white hover:brightness-110" : "bg-[#0F2537] text-white hover:bg-[#0F2537]/90"
                }`}>
                <Crown weight="fill" size={16} /> {busy === p.id ? "Loading…" : (user?.is_pro ? "Extend Pro" : (founderActive ? `Claim ${tierLabel.toLowerCase()} rate` : "Go Pro"))}
                <ArrowRight weight="bold" size={14} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="max-w-3xl mx-auto text-center text-xs text-[#0F2537]/60 px-4">
        Secure checkout powered by Stripe. We never see your card details.
      </div>

      <p className="text-center text-sm text-[#0F2537]/60">
        <Link to="/dashboard" className="hover:text-[#E05D3A]">← Back to home</Link>
      </p>
    </div>
  );
}

function FounderBanner({ founder }) {
  const pct = Math.min(100, Math.round((founder.redeemed / founder.total_spots) * 100));
  const lowStock = founder.remaining <= 10;
  const tier = founder.tier || "founder";
  const isFounder = tier === "founder";
  const eyebrow = isFounder ? "Launch offer" : "Early-adopter offer";
  const headline = founder.headline || (isFounder
    ? "50% off your first year of Bula Pro"
    : "10% off your first year of Bula Pro");
  const sub = founder.subhead || (isFounder
    ? `For the first ${founder.total_spots} founding members only`
    : `Next ${founder.total_spots} members only`);
  // Gradient: warm orange for founder, cool teal for early adopter.
  const gradient = isFounder
    ? "linear-gradient(135deg, #E05D3A 0%, #C8421C 100%)"
    : "linear-gradient(135deg, #1E5631 0%, #133D21 100%)";
  return (
    <div className="max-w-3xl mx-auto rise" data-testid="founder-banner" data-tier={tier}>
      <div className="relative overflow-hidden rounded-2xl text-white px-6 py-5 shadow-lg" style={{ background: gradient }}>
        <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 0% 0%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Flame weight="fill" size={26} className="shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] font-black tracking-widest uppercase opacity-90">{eyebrow}</div>
              <div className="font-heading font-black text-xl leading-tight mt-0.5">{headline}</div>
              <div className="text-xs opacity-85 mt-1">{sub} · FJ${founder.founder_amount} instead of FJ${founder.regular_amount}</div>
            </div>
          </div>
          <div className="text-right">
            <div className={`font-heading font-black text-3xl leading-none tabular-nums ${lowStock ? "animate-pulse" : ""}`} data-testid="founder-remaining">
              {founder.remaining}<span className="text-base opacity-70">/{founder.total_spots}</span>
            </div>
            <div className="text-[10px] font-black tracking-widest uppercase opacity-90 mt-1">spots left</div>
          </div>
        </div>
        <div className="relative mt-3 h-1.5 rounded-full bg-white/20 overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        {founder.seconds_remaining != null && founder.seconds_remaining > 0 && (
          <Countdown seconds={founder.seconds_remaining} />
        )}
      </div>
    </div>
  );
}

function Countdown({ seconds }) {
  const [s, setS] = React.useState(seconds);
  React.useEffect(() => {
    setS(seconds);
    const t = setInterval(() => setS((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [seconds]);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return (
    <div className="relative mt-3 flex items-center gap-2 text-[11px] font-bold opacity-90" data-testid="founder-countdown">
      <span className="opacity-75">Offer ends in</span>
      <span className="tabular-nums bg-white/15 px-2 py-0.5 rounded">{d}d</span>
      <span className="tabular-nums bg-white/15 px-2 py-0.5 rounded">{String(h).padStart(2, "0")}h</span>
      <span className="tabular-nums bg-white/15 px-2 py-0.5 rounded">{String(m).padStart(2, "0")}m</span>
      <span className="tabular-nums bg-white/15 px-2 py-0.5 rounded">{String(sec).padStart(2, "0")}s</span>
    </div>
  );
}
